<script setup>
import { computed, defineAsyncComponent, inject, ref, toRef } from 'vue'
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'
import { hasFields } from '@convenia/employee-organisms/SystemFields/helpers'
import schema from '../content/forms'
import { mapCustomDocument, documentMappers as mappers } from '../content/mappers'

import AdmissionDocumentCard from './AdmissionDocumentCard'
import DocumentFormModal from '../fragments/DocumentFormModal'

const props = defineProps({
  documents: {
    type: Object,
    default: () => ({})
  },

  customDocuments: {
    type: Object,
    default: () => ({})
  },

  options: {
    type: Object,
    default: () => ({})
  },

})

const emit = defineEmits([ 'edit', 'load-cities', 'submit' ])

const documents = toRef(props, 'documents')
const options = toRef(props, 'options')

const employeeId = inject('employeeId')
const getFileUrl = inject('getFileUrl')

const DeleteModal = defineAsyncComponent(() => import('@convenia/common-organisms/Modals/Delete'))

const isEditing = ref(false)
const isSubmitting = ref(false)
const showRemoveFileModal = ref(false)
const filesFormData = ref({})
const active = ref(null)
const selected = ref({
  schema: {},
  systemFields: {},
  customFields: {},
  files: [],
  data: {},
})

function getCities (state) {
  if (!state)
    return

  emit('load-cities', state)
}

const schemas = computed(() => schema({ getCities }))

const mapDocument = (key, document) => {
  const schema = schemas.value[key] || {}
  const documentFiles = document?.data?.documentFiles || document?.files || []
  const data = { ...(document?.data || {}), documentFiles }

  return { ...document, data, files: documentFiles, schema }
}

const mappedDocuments = computed(() => {
  return Object.entries(documents.value)
    .filter(([ , document ]) => hasFields(document))
    .reduce((acc, [ key, document ]) => ({
      ...acc,
      [key]: mapDocument(key, document)
    }), {})
})

const filteredDocuments = computed(() => (
  Object.entries(mappedDocuments.value || {})
    .reduce((acc, [ key, document ]) => ({
      ...acc,
      ...(key === 'dismissal' ? {} : { [key]: document })
    }), {})
))

const value = computed(() => {
  if (!active.value)
    return {}

  return mappedDocuments.value[active.value] || {}
})

const onEdit = (key, document) => {
  active.value = key
  selected.value = document
  isEditing.value = true

  const { state, issuingState } = value.value.data || {}
  getCities(state?.id || issuingState?.id)

  emit('edit', key)
}

function validateDocument (type, document) {
  const {
    getFormValues,
    requiredFields: allRequiredFields,
  } = useSystemFields({
    schema: computed(() => document.schema),
    systemFields: computed(() => document.systemFields),
    customFields: computed(() => document.customFields),
  })

  const filterAttachments = key => {
    const customField = document?.customFields?.[key]

    return key === 'attachment' || customField?.type === 'attachment'
  }

  const mapper = mappers[type] ?? mapCustomDocument
  const data = mapper(document?.data || {}, { employeeId, getFileUrl })
  const values = getFormValues(data, document?.customFieldValues || {})
  const attachmentFields = allRequiredFields.value?.filter(filterAttachments) || []
  const requiredFiles = attachmentFields.length > 0
  const requiredFields = allRequiredFields.value?.filter(key => !attachmentFields.includes(key))

  let valid = true

  if (requiredFiles && !document?.files?.length)
    valid = false

  if (requiredFields.some(key => !values[key]))
    valid = false

  return valid
}

function validate () {
  return Object.entries(filteredDocuments.value)
    .reduce((acc, [ type, document ]) => {
      const valid = document.isOptional || validateDocument(type, document)

      if (!valid)
        acc.errors.push(type)
      else
        acc.validated.push(type)

      return { ...acc }
    }, { errors: [], validated: [] })
}

function clear () {
  active.value = null
  selected.value = {
    systemFields: {},
    customFields: {},
    files: [],
    data: {},
  }
  isEditing.value = false
  showRemoveFileModal.value = false
  filesFormData.value = {}
}

function verify () {
  const { validated = [] } = validate() || {}

  const verified = validated.filter(key => {
    const document = filteredDocuments.value[key]

    if (document.status === 'success')
      return true

    return !!document?.id
  })

  const errors = Object.keys(filteredDocuments.value)
    .filter(key => {
      const document = filteredDocuments.value[key]

      return document.status === 'error'
        && !verified.includes(key)
    })

  return { validated: verified, errors }
}

function onClose () {
  clear()
}

function onSubmit ({ data, files, replaced }) {
  const { id } = selected.value || {}

  if (replaced.length > 0 && !showRemoveFileModal.value) {
    filesFormData.value = { data, files, replaced }
    showRemoveFileModal.value = true

    return
  }

  showRemoveFileModal.value = false
  isSubmitting.value = true

  const callback = (err) => {
    isSubmitting.value = false

    if (err) return

    onClose()
  }

  const key = active.value
  const initialFiles = selected.value?.files || []
  const addedFiles = files.filter(file =>
    !!file.name && !initialFiles.find(({ id }) => file.id === id)
  )
  const replacedFiles = replaced
  const customFields = Object.keys(selected.value?.customFields || {})

  emit('submit', {
    data,
    id,
    key,
    addedFiles,
    files,
    initialFiles,
    replacedFiles,
    customFields,
    callback
  })
}

defineExpose({ validate, verify, clear, onClose })
</script>

<template>
  <div class="admission-documents-step">
    <c-title dark-text>
      Documentos
    </c-title>

    <div class="documents">
      <admission-document-card
        v-for="(document, key) in filteredDocuments"
        :key="`document-card-${document.id || key}`"
        :title="document.title"
        :files="(document.files || []).length"
        :error="document.status === 'error'"
        :success="document.status === 'success'"
        @click="onEdit(key, document)"
      />
    </div>

    <document-form-modal
      v-if="isEditing"
      :value="value"
      :type="active"
      :schema="value.schema"
      :system-fields="value.systemFields"
      :custom-fields="value.customFields"
      :loading="isSubmitting"
      :options="options"
      @close="onClose"
      @submit="onSubmit"
    />

    <delete-modal
      v-if="showRemoveFileModal"
      title="Deseja prosseguir?"
      message="Ao prosseguir os arquivos apagados serão permanentemente excluídos,
        esta ação não pode ser desfeita."
      button-message="Excluir"
      cancel-message="Não, mudei de ideia"
      :is-loading="isSubmitting"
      @cancel="showRemoveFileModal = false"
      @delete="onSubmit(filesFormData)"
    />
  </div>
</template>

<style lang="scss" scoped>
.admission-documents-step {
  position: relative;

  & > .c-title {
    padding-bottom: 20px;
  }

  & > .documents {
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 20px;
    row-gap: 20px;
    & > .document-card { min-width: 0; }
  }

  @include responsive (tablet, desktop) {
    & > .documents {
      grid-template-columns: 1fr 1fr;
    }
  }

  @include responsive (xs-mobile, mobile) {
    & > .c-title {
      display: none;
    }

    & > .documents {
      padding: 20px 10px;
      row-gap: 10px;
    }
  }
}
</style>
